"use client";

import React, { useEffect, useState } from "react";
import { SpacingType, sectionVariants } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { Button } from "@/components/ui/button";
import { builder } from "@builder.io/sdk";
import { BuilderContent } from "@builder.io/sdk";

// Ensure builder SDK is initialized for client-side calls
// The builder-registry.ts initializes it, but ensure it's available for client components
if (typeof window !== 'undefined') {
  builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);
}

interface FeaturedItem {
  image?: string;
  imageAltText?: string;
  badge?: {
    topic?: string;
  }[];
  title?: string;
  content?: string;
  link?: string;
}

interface Hero7Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    heroItems?: FeaturedItem[];
    featuredItems?: FeaturedItem[];
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero7 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero7Props) => {
  const [heroItems, setHeroItems] = useState<FeaturedItem[]>([]);
  const [featuredItems, setFeaturedItems] = useState<FeaturedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMounted, setIsMounted] = useState(false);

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return; // Wait for client mount
    
    // If data is provided via props (for visual editor), use it directly
    if (contents?.heroItems && contents?.featuredItems) {
      setHeroItems(contents.heroItems);
      setFeaturedItems(contents.featuredItems);
      setLoading(false);
      return;
    }

    // Otherwise, fetch data from API
    const fetchAllItems = async () => {
      try {
        // Fetch both hero items and featured items in parallel
        // Remove preview: true for client-side calls to work in all environments
        const [heroItems, featuredArticles] = await Promise.all([
          builder.getAll('market-insights', {
            limit: 1,
            query: {
              'data.heroArticle': true
            },
            options: {
              noTargeting: true,
            },
            noTraverse: true,
            noEditorUpdates: true,
            prerender: false,
          }),
          builder.getAll('market-insights', {
            limit: 3,
            query: {
              'data.featured': true
            },
            options: {
              noTargeting: true,
            },
            noTraverse: true,
            noEditorUpdates: true,
            prerender: false,
          })
        ]);

        // Transform hero items
        const transformedHeroItems: FeaturedItem[] = heroItems.map((item: BuilderContent) => ({
          image: item.data?.image,
          imageAltText: item.data?.imageAltText || item.name,
          badge: item.data?.topics,
          title: item.name,
          content: item.data?.content || item.data?.description,
          link: item.data?.url,
        }));

        // Transform featured items
        const transformedFeaturedItems: FeaturedItem[] = featuredArticles.map((article: BuilderContent) => ({
          image: article.data?.image,
          imageAltText: article.data?.imageAltText || article.name,
          badge: article.data?.topics,
          title: article.name,
          content: article.data?.content || article.data?.description,
          link: article.data?.url,
        }));

        setHeroItems(transformedHeroItems);
        setFeaturedItems(transformedFeaturedItems);
      } catch (error) {
        console.error("Error fetching items:", error);
        setHeroItems([]);
        setFeaturedItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllItems();
  }, [contents?.heroItems, contents?.featuredItems, isMounted]);

  if (loading) {
    return (
      <SectionWrapper 
        spacing={spacing}
        variant={variant}
        darkMode={darkMode}
        heroSection={true}
        componentName="hero7" 
        attributes={attributes}
        usedInPageNav={false}
        pageNavTitle="Hero 7"
      >
        <div className="container flex flex-col gap-12 lg:gap-20">
          <div className="flex flex-col gap-6 text-center lg:text-left items-center lg:items-start">
            {contents?.badge && (
              <Badge variant="outline" bold>
                {contents?.badge}
              </Badge>
            )}
            {contents?.title && (
              <h1 className="text-gradient-primary">
                {parse(contents.title)}
              </h1>
            )}
          </div>
          
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-16">
            <div className="flex flex-col gap-6">
              <div className="animate-pulse">
                <div className="h-84 bg-muted rounded-xl aspect-[636/358]"></div>
              </div>
            </div>
            <div className="flex flex-col gap-4 justify-between">
              <div className="flex flex-col gap-4">
                <div className="animate-pulse">
                  <div className="h-6 bg-muted rounded-xl"></div>
                </div>
                <div className="animate-pulse">
                  <div className="h-6 bg-muted rounded-xl"></div>
                </div>
                <div className="animate-pulse">
                  <div className="h-6 bg-muted rounded-xl"></div>
                </div>
              </div>
              <div className="animate-pulse">
                <div className="h-12 bg-muted rounded-xl"></div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col">
            <h2 className="mb-6 md:mb-4">Featured stories.</h2>
            { Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="animate-pulse"
              >
                <div
                  className="flex flex-col py-6 md:py-8 md:flex-row gap-6 md:gap-16"
                >
                  <div className="flex-shrink-0 md:w-60">
                    <div className="h-40 bg-muted rounded-xl aspect-[249/140]">
                    </div>
                  </div>
                  <div className="flex flex-col gap-4 justify-between w-full">
                    <div className="flex flex-col gap-4">
                      <div className="h-6 bg-muted rounded-xl"></div>
                      <div className="h-6 bg-muted rounded-xl"></div>
                    </div>
                    <div className="h-6 bg-muted rounded-xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero7" 
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 7"
    >     
      <div className="container flex flex-col gap-12 lg:gap-20"> 
        <div className="flex flex-col gap-6 text-center lg:text-left items-center lg:items-start">
          {contents?.badge && (
            <Badge variant="outline" bold>
              {contents?.badge}
            </Badge>
          )}
          {contents?.title && (
            <h1 className="text-gradient-primary">
              {parse(contents.title)}
            </h1>
          )}
        </div>
        {heroItems.length > 0 && (
          <a href={heroItems[0].link ?? ''} tabIndex={-1} className="flex flex-col lg:flex-row gap-6 lg:gap-16">
            <div className="flex-shrink-0 w-full lg:w-1/2">
              {heroItems[0].image && (
                <div className="relative z-1 aspect-[636/358] w-full h-full">
                  <Image 
                    src={heroItems[0].image} 
                    alt={heroItems[0].imageAltText ?? ""} 
                    title={heroItems[0].imageAltText ?? ""}
                    className="rounded-xl object-cover"
                    fill
                    priority
                    fetchPriority="high"
                  />
                  <div className="absolute top-0 left-0 w-full h-full rounded-xl">
                    <Image src="/gradient-mask.svg" alt="gradient" width={505} height={285} className="aspect-[505/285] h-full object-fill relative w-full rounded-xl z-10" priority fetchPriority="high" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4 justify-between w-full lg:w-1/2">
              <div className="flex flex-col gap-4">
                {heroItems[0].title && (
                  <h3>
                    {parse(heroItems[0].title)}
                  </h3>
                )}
                {heroItems[0].content && (
                  <div className="paragraph-lg text-muted-foreground hidden mb-2 [&_p]:line-clamp-2 xl:[&_p]:line-clamp-3 lg:line-clamp-2 xl:line-clamp-3">
                    {parse(heroItems[0].content)}
                  </div>
                )}
                {heroItems[0].badge && (
                  <div className="flex flex-row gap-2 flex-wrap">
                    {heroItems[0].badge?.map((badge: { topic?: string }, index: number) => (
                      <Badge variant="outline" bold key={index}>
                        {badge.topic || ''}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {heroItems[0].link && (
                <Button variant="outline" size="iconLg" className="hidden lg:block">
                  <span className="material-symbols-outlined">arrow_forward</span>
                </Button>
              )}
            </div>
          </a>
        )}
        {featuredItems.length > 0 && (
          <div className="flex flex-col">
            <h2 className="mb-6 md:mb-4">Featured stories.</h2>
            {featuredItems.map((item, index) => (
              <a
                href={item.link ?? ''}
                tabIndex={-1}
                key={index}
                className={`${index !== featuredItems.length - 1 ? "md:border-b" : ""}`}
              >
                <div
                  className="flex flex-col py-6 md:py-8 md:flex-row gap-6 md:gap-16"
                >
                  <div className="flex-shrink-0 md:w-60">
                    {item.image && (
                      <div className="relative aspect-[249/140] h-full rounded-lg w-full z-1">
                        <Image
                          src={item.image}
                          alt={item.imageAltText ?? ''}
                          title={item.imageAltText ?? ''}
                          className="rounded-lg object-cover"
                          fill
                          priority
                          fetchPriority="high"
                        />
                        <div className="absolute top-0 left-0 w-full h-full">
                          <Image src="/gradient-mask.svg" alt="gradient" width={505} height={285} className="aspect-[505/285] h-full object-fill relative w-full rounded-lg z-10" priority fetchPriority="high" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col justify-between gap-2">
                    <div>
                      {item.title && (
                        <h5>
                          {parse(item.title)}
                        </h5>
                      )}
                      {item.content && (
                        <div className="paragraph-lg text-muted-foreground line-clamp-2 hidden sm:block">
                          {parse(item.content)}
                        </div>
                      )}
                    </div>
                    {item.badge && (
                      <div className="flex flex-row gap-2 flex-wrap">
                        {item.badge?.map((badge: { topic?: string }, index: number) => (
                          <Badge variant="outline" bold key={index}>
                            {badge.topic || ''}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export { Hero7 };
